import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Carousel1, Carousel2, Carousel3 } from '../../images';
import { Title } from '../title/Title';

export const Carousel = () => {
    const { t } = useTranslation();


    const slides = [
        {
            id: 1,
            image: Carousel1
        },
        {
            id: 2,
            image: Carousel2
        },
        {
            id: 3,
            image: Carousel3
        }
    ];

    const [currentSlide, setCurrentSlide] = useState(0);
    const [transitionDirection, setTransitionDirection] = useState<'right' | 'left'>('right');
    const [autoPlay] = useState(true);
    const staticTitle = (
        <div className='flex-col bg-gradient-to-r from-grey-900/20 to-grey-900/20 p-8 rounded-lg'>
            <h1 className="text-5xl font-bold text-white drop-shadow-lg text-center">
                <Title splitLines />
            </h1>

            <p className="text-gray-200 whitespace-pre-line mt-4 text-lg leading-relaxed text-center">
                {t('carousel.body').replace(/<br>/g, '\n')}
            </p>
        </div>
    )
    const slidesContainerRef = useRef<HTMLDivElement>(null);

    // Clone first and last slides for seamless looping
    const extendedSlides = [
        slides[slides.length - 1], // last slide at beginning
        ...slides,
        slides[0] // first slide at end
    ];

    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (autoPlay) {
            interval = setInterval(() => {
                handleNext();
            }, 5000);
        }

        return () => clearInterval(interval);
    }, [autoPlay, currentSlide]);

    const handleNext = () => {
        setTransitionDirection('right');
        setCurrentSlide(prev => {
            if (prev >= slides.length - 1) {
                // When reaching the end, jump to clone without animation
                setTimeout(() => {
                    setCurrentSlide(0);
                    if (slidesContainerRef.current) {
                        slidesContainerRef.current.style.transition = 'none';
                        slidesContainerRef.current.style.transform = `translateX(-${100}%)`;
                        // Force reflow
                        void slidesContainerRef.current.offsetWidth;
                        slidesContainerRef.current.style.transition = 'transform 700ms ease-in-out';
                    }
                }, 700);
                return prev + 1;
            }
            return prev + 1;
        });
    };

    const handlePrev = () => {
        setTransitionDirection('left');
        setCurrentSlide(prev => {
            if (prev <= 0) {
                // When at beginning, jump to end clone without animation
                setTimeout(() => {
                    setCurrentSlide(slides.length - 1);
                    if (slidesContainerRef.current) {
                        slidesContainerRef.current.style.transition = 'none';
                        slidesContainerRef.current.style.transform = `translateX(-${(slides.length) * 100}%)`;
                        // Force reflow
                        void slidesContainerRef.current.offsetWidth;
                        slidesContainerRef.current.style.transition = 'transform 700ms ease-in-out';
                    }
                }, 700);
                return prev - 1;
            }
            return prev - 1;
        });
    };

    const goToSlide = (index: number) => {
        setTransitionDirection(index > currentSlide ? 'right' : 'left');
        setCurrentSlide(index);
    };

    // Calculate the actual slide position (offset by 1 because of the clone)
    const actualPosition = currentSlide + 1;

    return (
        <div className="relative w-full h-[80vh] overflow-hidden hidden md:block">
            <div
                ref={slidesContainerRef}
                className="flex h-full transition-transform duration-700 ease-in-out"
                style={{ transform: `translateX(-${actualPosition * 100}%)` }}
            >
                {extendedSlides.map((slide, index) => (
                    <div key={`${slide.id}-${index}`} className="w-full flex-shrink-0 relative">
                        <img
                            src={slide.image}
                            alt=""
                            className="w-full h-full object-cover"
                        />
                    </div>
                ))}
            </div>

            {/* Static title overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-green-900/30 backdrop-blur-sm p-8 rounded-lg max-w-4xl mx-auto">
                    {staticTitle}
                </div>
            </div>

            {/* Navigation arrows */}
            <button
                onClick={handlePrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-40 hover:bg-opacity-60 text-white p-3 rounded-full transition-all"
                aria-label={`Previous Slide`}
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            <button
                onClick={handleNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-40 hover:bg-opacity-60 text-white p-3 rounded-full transition-all"
                aria-label={`Next Slide`}
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </button>

            {/* Indicators */}
            <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
                {slides.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`w-3 h-3 rounded-full transition-all ${index === currentSlide ? 'bg-white' : 'bg-white bg-opacity-40'}`}
                        aria-label={`Go to slide ${index + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};
